/**
 * Store Service
 * 
 * Provides comprehensive store management functionality including:
 * - CRUD operations for stores
 * - Filtering and searching
 * - Bulk operations
 * - Statistics and analytics
 * - Error handling and validation
 * 
 * @module StoreService
 */

import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  Timestamp,
  writeBatch,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Store,
  StoreFilters,
  StoreSort,
  StoreStatistics,
  StorePayload,
  StoreActionResult,
  BulkStoreAction,
  StoreAssignment,
  StoreStatus,
  StoreHistoryEntry
} from '../@types/store';

/**
 * Store Service Class
 * Handles all store-related operations with comprehensive error handling
 */
export class StoreService {
  private static readonly STORES_COLLECTION = 'Stores';
  private static readonly STORE_HISTORY_COLLECTION = 'StoreHistory';

  // Cache for filter values (field -> { values, cachedAt })
  private static filterValuesCache = new Map<string, { values: string[]; cachedAt: number }>();
  private static readonly FILTER_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

  // Cache for notes data (storeId -> { count, lastDate, cachedAt })
  private static notesDataCache = new Map<string, { count: number; lastDate: Date | Timestamp | null; cachedAt: number }>();
  private static readonly NOTES_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Invalidate filter values cache
   * Call this when stores are updated to refresh filter dropdowns
   */
  static invalidateFilterCache(): void {
    this.filterValuesCache.clear();
    console.log('[StoreService] Filter values cache cleared');
  }

  /**
   * Invalidate notes data cache for a specific store
   * Call this when a note is added/updated/deleted
   */
  static invalidateNotesCache(storeId?: string): void {
    if (storeId) {
      this.notesDataCache.delete(storeId);
      console.log(`[StoreService] Notes cache cleared for store ${storeId}`);
    } else {
      this.notesDataCache.clear();
      console.log('[StoreService] All notes cache cleared');
    }
  }

  /**
   * Get a single store by ID
   * 
   * @param storeId - The unique identifier of the store
   * @returns Promise resolving to the store data or null if not found
   * @throws Error if database operation fails
   */
  static async getStoreById(storeId: string): Promise<Store | null> {
    try {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      const storeRef = doc(db, this.STORES_COLLECTION, storeId);
      const storeSnap = await getDoc(storeRef);

      if (!storeSnap.exists()) {
        console.warn(`Store not found: ${storeId}`);
        return null;
      }

      return this.mapDbDataToStore(storeSnap.id, storeSnap.data());
    } catch (error: any) {
      console.error('Error fetching store:', error);
      throw new Error(`Failed to fetch store: ${error.message}`);
    }
  }

  /**
   * Get all stores with optional filtering and sorting
   * 
   * @param filters - Optional filters to apply
   * @param sort - Optional sorting configuration
   * @param pageSize - Number of results per page (default: 50)
   * @param lastDoc - Last document for pagination
   * @returns Promise resolving to array of stores
   * @throws Error if database operation fails
   */
  static async getStores(
    filters?: StoreFilters,
    sort?: StoreSort,
    pageSize: number = 50,
    lastDoc?: DocumentSnapshot
  ): Promise<{ stores: Store[]; hasMore: boolean; lastDoc?: DocumentSnapshot }> {
    try {
      // SEARCH STRATEGY:
      // When searchTerm is provided, fetch ALL stores and filter client-side
      // Firestore doesn't support case-insensitive "contains" search
      // So we must fetch all and filter in memory
      const isSearching = !!filters?.searchTerm?.trim();
      
      // Check if any filters are active (not just searchTerm)
      const hasActiveFilters = !!(
        filters?.customerNo || 
        filters?.storeName || 
        filters?.salesRepId || 
        filters?.city || 
        filters?.state || 
        filters?.salesGroup || 
        filters?.region || 
        filters?.tags
      );
      
      // For search or large page sizes (computed field sorting), fetch more stores
      // For normal browsing, use the requested page size
      // When pageSize > 50, it's likely for computed field sorting (notesCount, lastVisit) or filtering
      // When filters are active, we want to fetch ALL matching stores, so use the requested pageSize (up to 9999)
      // We cap at 9999 so that effectivePageSize + 1 doesn't exceed the Firestore limit of 10000
      const isLargeFetch = pageSize > 50;
      const effectivePageSize = (isSearching && !lastDoc) || (isLargeFetch && !hasActiveFilters) 
        ? Math.min(pageSize, 9999) 
        : (hasActiveFilters ? Math.min(pageSize, 9999) : pageSize);
      
      const constraints: QueryConstraint[] = [];

      // Apply database-level filters (NOT searchTerm - that's handled client-side)
      if (filters) {
        // Customer number filter - database level (supports multi-select with 'in' operator)
        if (filters.customerNo) {
          const customerNos = Array.isArray(filters.customerNo) ? filters.customerNo : [filters.customerNo];
          const customerNoValues = customerNos.map(no => {
            const str = String(no).trim();
            const num = Number(str);
            return (!isNaN(num) && String(num) === str) ? num : str;
          });
          
          const allNumbers = customerNoValues.every(v => typeof v === 'number');
          const allStrings = customerNoValues.every(v => typeof v === 'string');
          
          if (customerNoValues.length === 1) {
            constraints.push(where('CustomerNo', '==', customerNoValues[0]));
          } else if (customerNoValues.length > 1 && customerNoValues.length <= 30 && (allNumbers || allStrings)) {
            // Firestore supports up to 30 items in 'in' queries
            constraints.push(where('CustomerNo', 'in', customerNoValues));
          } else if (customerNoValues.length > 30) {
            // Too many values for Firestore 'in' query.
            // Skip database-level filter and rely on client-side filtering
            // since we fetch a large batch (up to 9999) when filters are active.
            console.log(`[StoreService] Too many CustomerNo values (${customerNoValues.length}), skipping DB-level filter`);
          } else if (customerNoValues.length > 1) {
            const stringValues = customerNoValues.map(v => String(v));
            constraints.push(where('CustomerNo', 'in', stringValues.slice(0, 30)));
          }
        }

        // Sales rep filter - database level (supports multi-select with 'in' operator)
        if (filters.salesRepId) {
          const salesRepIds = Array.isArray(filters.salesRepId) ? filters.salesRepId : [filters.salesRepId];
          if (salesRepIds.length === 1) {
            constraints.push(where('SalespersonNo', '==', salesRepIds[0]));
          } else if (salesRepIds.length > 1 && salesRepIds.length <= 30) {
            constraints.push(where('SalespersonNo', 'in', salesRepIds));
          } else if (salesRepIds.length > 30) {
            console.log(`[StoreService] Too many SalespersonNo values (${salesRepIds.length}), skipping DB-level filter`);
          }
        }

        // Region filter - database level
        if (filters.region) {
          const regions = Array.isArray(filters.region) ? filters.region : [filters.region];
          if (regions.length === 1) {
            constraints.push(where('region', '==', regions[0]));
          } else if (regions.length > 1 && regions.length <= 30) {
            constraints.push(where('region', 'in', regions));
          } else if (regions.length > 30) {
            console.log(`[StoreService] Too many region values (${regions.length}), skipping DB-level filter`);
          }
        }

        // City filter - database level (supports multi-select)
        if (filters.city) {
          const cities = Array.isArray(filters.city) ? filters.city : [filters.city];
          if (cities.length === 1) {
            constraints.push(where('City', '==', cities[0]));
          } else if (cities.length > 1 && cities.length <= 30) {
            constraints.push(where('City', 'in', cities));
          } else if (cities.length > 30) {
            console.log(`[StoreService] Too many city values (${cities.length}), skipping DB-level filter`);
          }
        }

        // State filter - database level (supports multi-select)
        if (filters.state) {
          const states = Array.isArray(filters.state) ? filters.state : [filters.state];
          if (states.length === 1) {
            constraints.push(where('Prov', '==', states[0]));
          } else if (states.length > 1 && states.length <= 30) {
            constraints.push(where('Prov', 'in', states));
          } else if (states.length > 30) {
            console.log(`[StoreService] Too many state values (${states.length}), skipping DB-level filter`);
          }
        }

        // Tags filter - database level
        if (filters.tags && filters.tags.length > 0) {
          constraints.push(where('tags', 'array-contains-any', filters.tags));
        }

        // Sales group filter - database level (supports multi-select)
        if (filters.salesGroup) {
          const salesGroups = Array.isArray(filters.salesGroup) ? filters.salesGroup : [filters.salesGroup];
          if (salesGroups.length === 1) {
            constraints.push(where('SalesGroup', '==', salesGroups[0]));
          } else if (salesGroups.length > 1 && salesGroups.length <= 30) {
            constraints.push(where('SalesGroup', 'in', salesGroups));
          } else if (salesGroups.length > 30) {
            console.log(`[StoreService] Too many salesGroup values (${salesGroups.length}), skipping DB-level filter`);
          }
        }

        // Store name filter - database level (supports multi-select)
        if (filters.storeName) {
          const storeNames = Array.isArray(filters.storeName) ? filters.storeName : [filters.storeName];
          if (storeNames.length === 1) {
            constraints.push(where('StoreName', '==', storeNames[0]));
          } else if (storeNames.length > 1 && storeNames.length <= 30) {
            constraints.push(where('StoreName', 'in', storeNames));
          } else if (storeNames.length > 30) {
            console.log(`[StoreService] Too many storeName values (${storeNames.length}), skipping DB-level filter`);
          }
        }

        // Sort Field filter - database level (supports multi-select)
        if (filters.sortField) {
          const sortFieldValues = Array.isArray(filters.sortField) ? filters.sortField : [filters.sortField];
          if (sortFieldValues.length === 1) {
            constraints.push(where('SortField', '==', sortFieldValues[0]));
          } else if (sortFieldValues.length > 1 && sortFieldValues.length <= 30) {
            constraints.push(where('SortField', 'in', sortFieldValues));
          } else if (sortFieldValues.length > 30) {
            console.log(`[StoreService] Too many sortField values (${sortFieldValues.length}), skipping DB-level filter`);
          }
        }

        // NOTE: searchTerm is NOT applied at database level
        // It will be filtered client-side after fetching to support "contains" matching
      }

      // Apply sorting - use actual database field names
      // For computed fields (notesCount, lastVisit), we'll sort client-side after fetching notes
      // Database-level sorting requires denormalized fields to be present on all documents
      if (sort) {
        const isComputedField = sort.field === 'notesCount' || sort.field === 'lastVisit';
        
        if (isComputedField) {
          // Don't add orderBy for computed fields - we'll sort client-side after computing
          // This ensures accuracy even if denormalized fields are missing
          // Default to StoreName for the database query, then sort client-side
          constraints.push(orderBy('StoreName', 'asc'));
        } else {
          // For regular fields, use database-level sorting
          const dbField = this.mapSortFieldToDbField(sort.field);
          constraints.push(orderBy(dbField, sort.direction));
        }
      } else {
        // Default sorting by StoreName
        constraints.push(orderBy('StoreName', 'asc'));
      }

      // Add pagination
      // Firestore has a hard limit of 10,000 for structured queries
      // We add +1 to check if there are more results, but cap it at 10,000
      const queryLimit = Math.min(effectivePageSize + 1, 10000);
      constraints.push(limit(queryLimit)); 

      // Add cursor for pagination
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const storesRef = collection(db, this.STORES_COLLECTION);
      const q = query(storesRef, ...constraints);
      
      console.log('[StoreService] Fetching stores...', { 
        isSearching, 
        effectivePageSize, 
        hasSearchTerm: !!filters?.searchTerm,
        searchTerm: filters?.searchTerm 
      });
      const startTime = Date.now();
      
      const querySnapshot = await getDocs(q);
      
      console.log(`[StoreService] Fetched ${querySnapshot.docs.length} stores in ${Date.now() - startTime}ms`);
      
      const docs = querySnapshot.docs;
      // If we got exactly 10,000 docs, we assume there might be more (since we capped the query limit at 10,000)
      // Otherwise, check if we got more than the requested effectivePageSize
      const hasMore = queryLimit >= 10000 ? docs.length >= 10000 : docs.length > effectivePageSize;

      // Process results (exclude the extra document if present and we're under the hard limit)
      const docsToProcess = (hasMore && queryLimit < 10000) ? docs.slice(0, -1) : docs;
      
      // Map stores first (fast operation)
      const stores = docsToProcess.map(doc => {
        const data = doc.data();
        return this.mapDbDataToStore(doc.id, data);
      });

      // Always fetch notes data to ensure accurate visit counts and dates
      // This is necessary because denormalized fields may not exist on all stores yet
      const storeIds = stores.map(s => s.id);
      const isSortingByComputedField = sort && (sort.field === 'notesCount' || sort.field === 'lastVisit');
      
      console.log('[StoreService] Fetching notes data for stores...', { 
        storeCount: storeIds.length, 
        isSortingByComputedField,
        sortField: sort?.field 
      });
      
      // Always fetch notes data to compute accurate values
      const notesDataMap = await this.batchGetNotesData(storeIds);
      
      // Debug: Log notes data summary
      const storesWithNotes = Array.from(notesDataMap.entries()).filter(([_, data]) => data.count > 0);
      console.log(`[StoreService] Notes data summary: ${storesWithNotes.length} stores have notes out of ${storeIds.length} total stores`);
      if (storesWithNotes.length > 0) {
        console.log(`[StoreService] Sample stores with notes:`, storesWithNotes.slice(0, 3).map(([id, data]) => ({
          storeId: id,
          count: data.count,
          lastDate: data.lastDate ? (data.lastDate instanceof Timestamp ? data.lastDate.toDate().toISOString() : String(data.lastDate)) : null
        })));
      }
      
      // Apply notes data to stores
      let finalStores = stores.map(store => {
        const notesData = notesDataMap.get(store.id) || { count: 0, lastDate: null };
        store.notesCount = notesData.count;
        store.lastNoteDate = notesData.lastDate || undefined;
          
        if (notesData.lastDate) {
          store.lastVisit = notesData.lastDate;
        }
        
        // Debug: Log if store should have notes but doesn't
        if (notesData.count === 0 && storeIds.includes(store.id)) {
          // This is expected for stores without notes, so we don't log it
        }
        
        return store;
      });
      
      // If sorting by computed fields, sort client-side after computing values
      if (isSortingByComputedField && sort) {
        console.log('[StoreService] Sorting by computed field client-side:', sort.field, sort.direction);
        finalStores.sort((a, b) => {
          if (sort.field === 'notesCount') {
            const aCount = a.notesCount || 0;
            const bCount = b.notesCount || 0;
            return sort.direction === 'desc' ? bCount - aCount : aCount - bCount;
          } else if (sort.field === 'lastVisit') {
            const aDate = a.lastNoteDate || a.lastVisit;
            const bDate = b.lastNoteDate || b.lastVisit;
            
            if (!aDate && !bDate) return 0;
            if (!aDate) return 1; // No date goes to end
            if (!bDate) return -1;
            
            const aDateValue = aDate instanceof Date 
              ? aDate 
              : (aDate && typeof aDate === 'object' && 'toDate' in aDate && typeof aDate.toDate === 'function')
                ? aDate.toDate()
                : new Date(aDate as any);
            const bDateValue = bDate instanceof Date 
              ? bDate 
              : (bDate && typeof bDate === 'object' && 'toDate' in bDate && typeof bDate.toDate === 'function')
                ? bDate.toDate()
                : new Date(bDate as any);
            
            return sort.direction === 'desc' 
              ? bDateValue.getTime() - aDateValue.getTime()
              : aDateValue.getTime() - bDateValue.getTime();
          }
          return 0;
        });
      }
      
      // Update denormalized fields on store documents (async, don't wait)
      // This ensures future queries can use database-level sorting
      this.updateDenormalizedFields(storeIds, notesDataMap).catch(err => {
        console.warn('[StoreService] Failed to update denormalized fields:', err);
      });

      const storesWithVisits = finalStores.filter(s => (s.notesCount || 0) > 0).length;
      console.log(`[StoreService] ✓ Applied notes data to ${finalStores.length} stores (${storesWithVisits} with visits)`);
      
      // Debug: Log sample of stores with visits
      if (storesWithVisits > 0) {
        const sampleStores = finalStores.filter(s => (s.notesCount || 0) > 0).slice(0, 3);
        console.log('[StoreService] Sample stores with visits:', sampleStores.map(s => ({
          id: s.id,
          name: s.name,
          notesCount: s.notesCount,
          lastVisit: s.lastNoteDate || s.lastVisit
        })));
      } else {
        console.warn('[StoreService] ⚠️ No stores with visits found. Check if notes exist in database.');
      }

      // Apply client-side search filtering
      // Firestore doesn't support "contains" search, so we filter client-side
      let filteredStores = finalStores;

      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.trim().toLowerCase();
        
        if (searchLower.length > 0) {
          console.log('[StoreService] Searching for:', searchLower, 'in', finalStores.length, 'stores');
          
          filteredStores = finalStores.filter(store => {
            // Helper function to safely check if a field contains the search term (case-insensitive)
            const fieldContains = (field: any): boolean => {
              if (field === null || field === undefined) return false;
              return String(field).toLowerCase().includes(searchLower);
            };
            
            // Also try exact match for numeric fields like customer number
            const fieldMatches = (field: any): boolean => {
              if (field === null || field === undefined) return false;
              return String(field).toLowerCase() === searchLower;
            };
            
            return (
              // Store name (primary search field)
              fieldContains(store.name) ||
              // Customer number - both contains and exact match
              fieldContains(store.customerNo) ||
              fieldMatches(store.customerNo) ||
              // Store number
              fieldContains(store.storeNumber) ||
              // Address
              fieldContains(store.address) ||
              // City
              fieldContains(store.city) ||
              // Province/State (both fields for compatibility)
              fieldContains(store.state) ||
              fieldContains((store as any).province) ||
              fieldContains((store as any).Prov) ||
              // Sales group
              fieldContains(store.salesGroup) ||
              fieldContains((store as any).SalesGroup) ||
              // Salesperson number - both contains and exact match
              fieldContains(store.salespersonNo) ||
              fieldMatches(store.salespersonNo) ||
              fieldContains((store as any).SalespersonNo) ||
              // Sales person name (full name assigned)
              fieldContains(store.salesPersonName) ||
              fieldContains((store as any).SalesPersonName) ||
              // Region
              fieldContains(store.region) ||
              // Territory
              fieldContains(store.territory) ||
              // Phone/Telephone
              fieldContains(store.phone) ||
              fieldContains(store.telephone) ||
              fieldContains((store as any).Telephone) ||
              // Postal code
              fieldContains(store.pc) ||
              fieldContains((store as any).PostalCode) ||
              // Email
              fieldContains(store.email) ||
              fieldContains((store as any).EmailAddress) ||
              // Sort fields (sometimes store names are here)
              fieldContains(store.sortF5) ||
              fieldContains(store.sortField) ||
              // Store field
              fieldContains(store.store)
            );
          });
          
          console.log(`[StoreService] Found ${filteredStores.length} matching stores for search: "${filters.searchTerm}"`);
          if (filteredStores.length > 0) {
            console.log('[StoreService] Sample matching stores:', filteredStores.slice(0, 5).map(s => ({
              id: s.id,
              name: s.name,
              city: s.city,
              customerNo: s.customerNo
            })));
          }
        }
      }

      // Apply store level filter (requires fetching latest note for each store)
      if (filters?.storeLevelMin !== undefined || filters?.storeLevelMax !== undefined) {
        const minLevel = filters.storeLevelMin ?? 0;
        const maxLevel = filters.storeLevelMax ?? 100;
        
        // Filter stores based on their latest note's store level
        const storesWithLevel = await Promise.all(
          filteredStores.map(async (store) => {
            try {
              // Get the latest note for this store
              const notesRef = collection(db, this.STORES_COLLECTION, store.id, 'notes');
              const notesQuery = query(notesRef, orderBy('createdAt', 'desc'), limit(1));
              const notesSnapshot = await getDocs(notesQuery);
              
              if (!notesSnapshot.empty) {
                const latestNote = notesSnapshot.docs[0].data();
                const storeLevel = latestNote.storeLevel;
                
                // If store level is defined and within range, include the store
                if (storeLevel !== undefined && storeLevel >= minLevel && storeLevel <= maxLevel) {
                  return store;
                }
              }
              // If no note or no store level, exclude from results
              return null;
            } catch (error) {
              console.warn(`Failed to fetch latest note for store ${store.id}:`, error);
              // If we can't fetch the note, exclude the store
              return null;
            }
          })
        );
        
        filteredStores = storesWithLevel.filter((store): store is Store => store !== null);
      }

      // Determine if there are more results
      let finalHasMore = hasMore;
      
      // For search, we've fetched all stores and filtered client-side
      // So hasMore should be based on filtered results count
      if (filters?.searchTerm) {
        // When searching, we fetched a large batch and filtered client-side
        // There are more results if filtered count exceeds page size
        finalHasMore = filteredStores.length > pageSize;
        
        // Limit results to page size for display
        if (filteredStores.length > pageSize) {
          filteredStores = filteredStores.slice(0, pageSize);
        }
      }

      // Get the last document for pagination (the one before the extra one if hasMore)
      const lastDocForPagination = hasMore && docs.length > 1 
        ? docs[docs.length - 2] 
        : (docs.length > 0 ? docs[docs.length - 1] : undefined);

      return {
        stores: filteredStores,
        hasMore: finalHasMore,
        lastDoc: lastDocForPagination
      };
    } catch (error: any) {
      console.error('Error fetching stores:', error);
      throw new Error(`Failed to fetch stores: ${error.message}`);
    }
  }

  /**
   * Map interface field names to database field names
   * 
   * @param storeData - Store data with interface field names
   * @returns Store data with database field names
   * @private
   */
  private static mapStoreDataToDbFields(storeData: Partial<StorePayload>): any {
    const dbData: any = {};
    
    // Map interface fields to database fields
    if (storeData.name !== undefined) dbData.StoreName = storeData.name;
    if (storeData.address !== undefined) dbData.Address = storeData.address;
    if (storeData.city !== undefined) dbData.City = storeData.city;
    if (storeData.state !== undefined) dbData.Prov = storeData.state;
    if (storeData.pc !== undefined) dbData.PostalCode = storeData.pc;
    if (storeData.phone !== undefined) dbData.Telephone = storeData.phone;
    if (storeData.telephone !== undefined) dbData.Telephone = storeData.telephone;
    if (storeData.email !== undefined) dbData.EmailAddress = storeData.email;
    if (storeData.customerNo !== undefined) dbData.CustomerNo = storeData.customerNo;
    if (storeData.salesGroup !== undefined) dbData.SalesGroup = storeData.salesGroup;
    if (storeData.salespersonNo !== undefined) dbData.SalespersonNo = storeData.salespersonNo;
    if (storeData.salesPersonName !== undefined) dbData.SalesPersonName = storeData.salesPersonName;
    if (storeData.status !== undefined) dbData.Status = storeData.status;
    if (storeData.region !== undefined) dbData.Region = storeData.region;
    if (storeData.territory !== undefined) dbData.Territory = storeData.territory;
    if (storeData.tags !== undefined) dbData.Tags = storeData.tags;
    if (storeData.storeNumber !== undefined) dbData.StoreNumber = storeData.storeNumber;
    if (storeData.nextVisitDate !== undefined) dbData.nextVisitDate = storeData.nextVisitDate;
    
    // Keep other fields as-is (they might already be in DB format)
    Object.keys(storeData).forEach(key => {
      if (!['name', 'address', 'city', 'state', 'pc', 'phone', 'telephone', 'email', 
            'customerNo', 'salesGroup', 'salespersonNo', 'salesPersonName', 'status',
            'region', 'territory', 'tags', 'storeNumber', 'nextVisitDate'].includes(key)) {
        dbData[key] = (storeData as any)[key];
      }
    });
    
    return dbData;
  }

  /**
   * Create a new store
   * 
   * @param storeData - Store data payload
   * @param userId - ID of the user creating the store
   * @returns Promise resolving to action result with new store ID
   * @throws Error if validation fails or database operation fails
   */
  static async createStore(
    storeData: StorePayload,
    userId: string
  ): Promise<StoreActionResult> {
    try {
      // Validate required fields
      if (!storeData.name || !storeData.address || !storeData.city) {
        throw new Error('Name, address, and city are required');
      }

      // Generate customer number if not provided
      const customerNo = storeData.customerNo || await this.generateCustomerNumber();

      // Map interface field names to database field names
      const dbData = this.mapStoreDataToDbFields({
        ...storeData,
        customerNo
      });

      // Add metadata fields
      dbData.Status = storeData.status || 'pending';
      dbData.CreatedAt = Timestamp.now();
      dbData.LastUpdated = Timestamp.now();
      dbData.CreatedBy = userId;
      dbData.UpdatedBy = userId;
      dbData.TotalOrders = 0;
      dbData.TotalRevenue = 0;

      const storesRef = collection(db, this.STORES_COLLECTION);
      const docRef = await addDoc(storesRef, dbData);

      // Log history entry
      await this.logStoreHistory({
        storeId: docRef.id,
        action: 'updateInfo',
        performedBy: userId,
        performedByName: 'Manager', // Should be replaced with actual user name
        timestamp: new Date(),
        notes: 'Store created'
      });

      return {
        success: true,
        storeId: docRef.id,
        message: 'Store created successfully'
      };
    } catch (error: any) {
      console.error('Error creating store:', error);
      return {
        success: false,
        error: error.message || 'Failed to create store'
      };
    }
  }

  /**
   * Update an existing store
   * 
   * @param storeId - ID of the store to update
   * @param updates - Partial store data to update
   * @param userId - ID of the user updating the store
   * @returns Promise resolving to action result
   * @throws Error if store not found or database operation fails
   */
  static async updateStore(
    storeId: string,
    updates: Partial<StorePayload>,
    userId: string
  ): Promise<StoreActionResult> {
    try {
      if (!storeId) {
        throw new Error('Store ID is required');
      }

      // Get current store data for history tracking
      const currentStore = await this.getStoreById(storeId);
      if (!currentStore) {
        throw new Error('Store not found');
      }

      const storeRef = doc(db, this.STORES_COLLECTION, storeId);
      
      // Map interface field names to database field names
      const dbUpdateData = this.mapStoreDataToDbFields(updates);
      
      // Add metadata fields
      dbUpdateData.LastUpdated = Timestamp.now();
      dbUpdateData.UpdatedBy = userId;

      await updateDoc(storeRef, dbUpdateData);

      // Track changes for history
      const changes: Record<string, { old: any; new: any }> = {};
      Object.keys(updates).forEach(key => {
        if (currentStore[key as keyof Store] !== updates[key as keyof StorePayload]) {
          changes[key] = {
            old: currentStore[key as keyof Store],
            new: updates[key as keyof StorePayload]
          };
        }
      });

      // Log history entry
      await this.logStoreHistory({
        storeId,
        action: 'updateInfo',
        performedBy: userId,
        performedByName: 'Manager',
        timestamp: new Date(),
        changes,
        notes: 'Store information updated'
      });

      return {
        success: true,
        storeId,
        message: 'Store updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating store:', error);
      return {
        success: false,
        error: error.message || 'Failed to update store'
      };
    }
  }

  /**
   * Change store status (activate, deactivate, archive)
   * 
   * @param storeId - ID of the store
   * @param newStatus - New status to set
   * @param userId - ID of the user performing the action
   * @returns Promise resolving to action result
   */
  static async changeStoreStatus(
    storeId: string,
    newStatus: StoreStatus,
    userId: string
  ): Promise<StoreActionResult> {
    try {
      const storeRef = doc(db, this.STORES_COLLECTION, storeId);
      
      await updateDoc(storeRef, {
        status: newStatus,
        lastUpdated: Timestamp.now(),
        updatedBy: userId
      });

      // Log history entry
      await this.logStoreHistory({
        storeId,
        action: newStatus === 'active' ? 'activate' : 
                newStatus === 'inactive' ? 'deactivate' : 
                newStatus === 'archived' ? 'archive' : 'updateInfo',
        performedBy: userId,
        performedByName: 'Manager',
        timestamp: new Date(),
        notes: `Store status changed to ${newStatus}`
      });

      return {
        success: true,
        storeId,
        message: `Store ${newStatus} successfully`
      };
    } catch (error: any) {
      console.error('Error changing store status:', error);
      return {
        success: false,
        error: error.message || 'Failed to change store status'
      };
    }
  }

  /**
   * Delete a store (soft delete by archiving)
   * 
   * @param storeId - ID of the store to delete
   * @param userId - ID of the user performing the deletion
   * @param hardDelete - Whether to permanently delete (default: false)
   * @returns Promise resolving to action result
   */
  static async deleteStore(
    storeId: string,
    userId: string,
    hardDelete: boolean = false
  ): Promise<StoreActionResult> {
    try {
      if (hardDelete) {
        // Permanent deletion (use with caution)
        const storeRef = doc(db, this.STORES_COLLECTION, storeId);
        await deleteDoc(storeRef);
        
        return {
          success: true,
          storeId,
          message: 'Store permanently deleted'
        };
      } else {
        // Soft delete (archive)
        return await this.changeStoreStatus(storeId, 'archived', userId);
      }
    } catch (error: any) {
      console.error('Error deleting store:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete store'
      };
    }
  }

  /**
   * Assign a sales representative to a store
   * 
   * @param assignment - Store assignment data
   * @param userId - ID of the user performing the assignment
   * @returns Promise resolving to action result
   */
  static async assignSalesRep(
    assignment: StoreAssignment,
    userId: string
  ): Promise<StoreActionResult> {
    try {
      const storeRef = doc(db, this.STORES_COLLECTION, assignment.storeId);
      
      await updateDoc(storeRef, {
        salespersonNo: assignment.salesRepId,
        salesPersonName: assignment.salesRepName,
        lastUpdated: Timestamp.now(),
        updatedBy: userId
      });

      // Log history entry
      await this.logStoreHistory({
        storeId: assignment.storeId,
        action: 'assignSalesRep',
        performedBy: userId,
        performedByName: 'Manager',
        timestamp: new Date(),
        notes: `Assigned to ${assignment.salesRepName}${assignment.notes ? `: ${assignment.notes}` : ''}`
      });

      return {
        success: true,
        storeId: assignment.storeId,
        message: 'Sales representative assigned successfully'
      };
    } catch (error: any) {
      console.error('Error assigning sales rep:', error);
      return {
        success: false,
        error: error.message || 'Failed to assign sales representative'
      };
    }
  }

  /**
   * Perform bulk actions on multiple stores
   * 
   * @param bulkAction - Bulk action configuration
   * @param userId - ID of the user performing the action
   * @returns Promise resolving to results for each store
   */
  static async performBulkAction(
    bulkAction: BulkStoreAction,
    userId: string
  ): Promise<StoreActionResult[]> {
    const results: StoreActionResult[] = [];

    try {
      const batch = writeBatch(db);
      const timestamp = Timestamp.now();

      for (const storeId of bulkAction.storeIds) {
        try {
          const storeRef = doc(db, this.STORES_COLLECTION, storeId);

          switch (bulkAction.action) {
            case 'activate':
            case 'deactivate':
            case 'archive':
              batch.update(storeRef, {
                status: bulkAction.data?.status || 
                        (bulkAction.action === 'activate' ? 'active' : 
                         bulkAction.action === 'deactivate' ? 'inactive' : 'archived'),
                lastUpdated: timestamp,
                updatedBy: userId
              });
              break;

            case 'assignSalesRep':
              if (bulkAction.data?.salesRepId && bulkAction.data?.salesRepName) {
                batch.update(storeRef, {
                  salespersonNo: bulkAction.data.salesRepId,
                  salesPersonName: bulkAction.data.salesRepName,
                  lastUpdated: timestamp,
                  updatedBy: userId
                });
              }
              break;

            case 'addTag':
              if (bulkAction.data?.tag) {
                // Note: This requires fetching the current tags first
                const storeSnap = await getDoc(storeRef);
                const currentTags = storeSnap.data()?.tags || [];
                batch.update(storeRef, {
                  tags: [...currentTags, bulkAction.data.tag],
                  lastUpdated: timestamp,
                  updatedBy: userId
                });
              }
              break;

            default:
              console.warn(`Unsupported bulk action: ${bulkAction.action}`);
          }

          results.push({
            success: true,
            storeId,
            message: `${bulkAction.action} completed successfully`
          });
        } catch (error: any) {
          results.push({
            success: false,
            storeId,
            error: error.message
          });
        }
      }

      await batch.commit();

      return results;
    } catch (error: any) {
      console.error('Error performing bulk action:', error);
      throw new Error(`Bulk action failed: ${error.message}`);
    }
  }

  /**
   * Get store statistics for dashboard
   * 
   * @returns Promise resolving to store statistics
   */
  static async getStoreStatistics(): Promise<StoreStatistics> {
    try {
      const storesRef = collection(db, this.STORES_COLLECTION);
      const querySnapshot = await getDocs(storesRef);

      const stats: StoreStatistics = {
        totalStores: 0,
        activeStores: 0,
        inactiveStores: 0,
        pendingStores: 0,
        archivedStores: 0,
        storesVisitedToday: 0,
        storesVisitedThisWeek: 0,
        storesVisitedThisMonth: 0,
        averageOrdersPerStore: 0,
        averageRevenuePerStore: 0
      };

      const now = new Date();
      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      let totalOrders = 0;
      let totalRevenue = 0;

      querySnapshot.forEach((doc) => {
        const store = doc.data() as Store;
        stats.totalStores++;

        // Count by status
        switch (store.status) {
          case 'active':
            stats.activeStores++;
            break;
          case 'inactive':
            stats.inactiveStores++;
            break;
          case 'pending':
            stats.pendingStores++;
            break;
          case 'archived':
            stats.archivedStores++;
            break;
        }

        // Count visits
        if (store.lastVisit) {
          const lastVisit = store.lastVisit instanceof Timestamp 
            ? store.lastVisit.toDate() 
            : new Date(store.lastVisit);

          if (lastVisit >= todayStart) {
            stats.storesVisitedToday++;
          }
          if (lastVisit >= weekStart) {
            stats.storesVisitedThisWeek++;
          }
          if (lastVisit >= monthStart) {
            stats.storesVisitedThisMonth++;
          }
        }

        // Accumulate orders and revenue
        totalOrders += store.totalOrders || 0;
        totalRevenue += store.totalRevenue || 0;
      });

      // Calculate averages
      if (stats.totalStores > 0) {
        stats.averageOrdersPerStore = totalOrders / stats.totalStores;
        stats.averageRevenuePerStore = totalRevenue / stats.totalStores;
      }

      return stats;
    } catch (error: any) {
      console.error('Error calculating store statistics:', error);
      throw new Error(`Failed to calculate statistics: ${error.message}`);
    }
  }

  /**
   * Map database field names to interface field names for sorting
   * 
   * @param field - Interface field name
   * @returns Database field name
   * @private
   */
  private static mapSortFieldToDbField(field: string): string {
    const fieldMap: Record<string, string> = {
      'name': 'StoreName',
      'customerNo': 'CustomerNo',
      'lastVisit': 'lastVisit', // Denormalized field on store document
      'notesCount': 'notesCount', // Denormalized field on store document
      'totalOrders': 'totalOrders',
      'totalRevenue': 'totalRevenue',
      'city': 'City',
      'salespersonNo': 'SalespersonNo',
      'salesPersonName': 'SalespersonNo',
      'salesGroup': 'SalesGroup',
      'sortField': 'SortField'
    };
    
    return fieldMap[field] || field;
  }

  /**
   * Map database document data to Store interface
   * 
   * @param docId - Document ID
   * @param data - Raw database data
   * @returns Mapped Store object
   * @private
   */
  static mapDbDataToStore(docId: string, data: any): Store {
    // Get province/state value - check multiple possible field names
    const provinceValue = data.Prov || data.Province || data.prov || data.province || data.state || data.State || '';
    
    // Get denormalized fields (notesCount and lastVisit stored on store document)
    const notesCount = data.notesCount !== undefined ? Number(data.notesCount) : undefined;
    const lastVisitDenorm = data.lastVisit?.toDate?.() || data.lastVisit || undefined;
    
    const store: Store = {
      id: docId,
      // Map database fields to interface fields
      customerNo: String(data.CustomerNo || data.customerNo || ''),
      name: data.StoreName || data.Name || data.name || 'Unknown Store',
      address: data.Address || data.address || '',
      city: data.City || data.city || '',
      state: provinceValue, // Province/State
      province: provinceValue, // Also add province for search compatibility
      pc: data.PostalCode || data.PC || data.pc || '',
      phone: data.Telephone || data.Phone || data.phone || '',
      email: data.EmailAdddress || data.EmailAddress || data.Email || data.email || '',
      salesGroup: data.SalesGroup || data.salesGroup || '',
      salespersonNo: String(data.SalespersonNo || data.salespersonNo || ''),
      salesPersonName: data.SalesPersonName || data.salesPersonName || '',
      sortF5: data.SortF5 || data.sortF5 || '',
      sortField: data.SortField || data.sortField || '',
      store: data.Store || data.store || '',
      status: data.Status || data.status || 'active',
      lastVisit: lastVisitDenorm || data.LastVisit?.toDate?.() || data.lastVisit,
      lastVisitedBy: data.LastVisitedBy || data.lastVisitedBy || '',
      // Use denormalized fields if available
      notesCount: notesCount,
      lastNoteDate: lastVisitDenorm,
      PickupNumber: data.PickupNumber || data.pickupNumber || '',
      PONumber: data.PONumber || data.poNumber || '',
      creditSheetCompleted: data.CreditSheetCompleted || data.creditSheetCompleted || false,
      Notes: data.Notes || data.notes || '',
      customerSignOffPerson: data.CustomerSignOffPerson || data.customerSignOffPerson || '',
      managerNotes: data.ManagerNotes || data.managerNotes || '',
      createdAt: data.CreatedAt?.toDate?.() || data.createdAt?.toDate?.() || data.createdAt,
      lastUpdated: data.LastUpdated?.toDate?.() || data.lastUpdated?.toDate?.() || data.lastUpdated,
      createdBy: data.CreatedBy || data.createdBy || '',
      updatedBy: data.UpdatedBy || data.updatedBy || '',
      totalOrders: data.TotalOrders || data.totalOrders || 0,
      totalRevenue: data.TotalRevenue || data.totalRevenue || 0,
      lastOrderDate: data.LastOrderDate?.toDate?.() || data.lastOrderDate?.toDate?.() || data.lastOrderDate,
      tags: data.Tags || data.tags || [],
      region: data.Region || data.region || '',
      territory: data.Territory || data.territory || '',
      // Additional fields from database
      storeNumber: data.StoreNumber || data.storeNumber || '',
      telephone: data.Telephone || data.telephone || '',
      nextVisitDate: data.nextVisitDate || data.nextVisitDate || ''
    } as Store;
    
    // Store raw data for denormalized field checks
    (store as any)._rawData = data;
    
    return store;
  }

  /**
   * Update denormalized fields (notesCount, lastVisit) on store documents
   * This allows database-level sorting and filtering for better performance
   * 
   * @param storeIds - Array of store IDs to update
   * @param notesDataMap - Map of storeId -> { count, lastDate }
   * @private
   */
  private static async updateDenormalizedFields(
    storeIds: string[],
    notesDataMap: Map<string, { count: number; lastDate: Date | Timestamp | null }>
  ): Promise<void> {
    if (storeIds.length === 0) return;

    try {
      const maxBatchSize = 500; // Firestore batch limit
      let processedCount = 0;

      // Process in batches
      for (let i = 0; i < storeIds.length; i += maxBatchSize) {
        const batch = writeBatch(db);
        const batchStoreIds = storeIds.slice(i, i + maxBatchSize);
        
        for (const storeId of batchStoreIds) {
          const notesData = notesDataMap.get(storeId) || { count: 0, lastDate: null };
          const storeRef = doc(db, this.STORES_COLLECTION, storeId);
          
          const updateData: any = {
            notesCount: notesData.count,
          };
          
          if (notesData.lastDate) {
            // Convert to Timestamp if it's a Date
            if (notesData.lastDate instanceof Date) {
              updateData.lastVisit = Timestamp.fromDate(notesData.lastDate);
            } else if (notesData.lastDate instanceof Timestamp) {
              updateData.lastVisit = notesData.lastDate;
            } else {
              updateData.lastVisit = Timestamp.fromDate(new Date(notesData.lastDate));
            }
          } else {
            // Set to null if no visits
            updateData.lastVisit = null;
          }
          
          batch.update(storeRef, updateData);
        }

        // Commit this batch
        await batch.commit();
        processedCount += batchStoreIds.length;
        console.log(`[StoreService] Updated denormalized fields for batch: ${processedCount}/${storeIds.length} stores`);
      }

      console.log(`[StoreService] ✓ Updated denormalized fields for ${storeIds.length} stores`);
    } catch (error: any) {
      console.error('[StoreService] Error updating denormalized fields:', error);
      // Don't throw - this is a background optimization
    }
  }

  /**
   * Update denormalized fields for a single store when a note is added/updated/deleted
   * Call this function whenever notes are modified to keep denormalized fields in sync
   * 
   * @param storeId - The store ID to update
   * @public
   */
  static async updateStoreDenormalizedFields(storeId: string): Promise<void> {
    try {
      // Fetch all notes for this store
      const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
      const notesSnapshot = await getDocs(notesRef);
      
      const notes = notesSnapshot.docs.map(doc => {
        const data = doc.data();
        return data.createdAt;
      }).filter(Boolean);

      const notesCount = notes.length;
      let lastVisit: Timestamp | null = null;

      if (notes.length > 0) {
        // Find the most recent date
        const dates = notes.map(n => {
          if (n instanceof Timestamp) return n.toDate();
          if (n instanceof Date) return n;
          if (n && typeof n === 'object' && 'toDate' in n) return (n as any).toDate();
          return new Date(n as any);
        });
        
        const mostRecent = dates.reduce((latest, current) => {
          return current > latest ? current : latest;
        }, dates[0]);
        
        lastVisit = Timestamp.fromDate(mostRecent);
      }

      // Update store document
      const storeRef = doc(db, this.STORES_COLLECTION, storeId);
      await updateDoc(storeRef, {
        notesCount,
        lastVisit: lastVisit || null
      });

      console.log(`[StoreService] Updated denormalized fields for store ${storeId}: ${notesCount} notes, lastVisit: ${lastVisit?.toDate()}`);
    } catch (error: any) {
      console.error(`[StoreService] Error updating denormalized fields for store ${storeId}:`, error);
      // Don't throw - this is a background operation
    }
  }

  /**
   * Get the count of notes for a specific store
   * 
   * @param storeId - The store ID
   * @returns Promise resolving to the number of notes
   * @private
   */
  /**
   * Get unique filter values for a specific field from all stores in database
   * This is used to populate filter dropdowns with all possible values
   * 
   * @param field - The database field name (e.g., 'City', 'Prov', 'CustomerNo')
   * @param maxValues - Maximum number of unique values to return (default: 1000)
   * @returns Promise resolving to array of unique values
   */
  /**
   * Get unique filter values with caching
   * Cached values are reused for 30 minutes to avoid expensive queries
   * 
   * @param field - Database field name
   * @param maxValues - Maximum number of values to return
   * @param searchTerm - Optional search term to filter values (searches in database)
   * @returns Promise resolving to array of unique values
   */
  static async getUniqueFilterValues(field: string, maxValues: number = 1000, searchTerm?: string): Promise<string[]> {
    const now = Date.now();
    const searchLower = searchTerm?.toLowerCase().trim();
    
    // For search queries, don't use cache - always query fresh from database
    // Cache is only used when loading all values without search
    if (!searchLower) {
      const cacheKey = `${field}_${maxValues}`;
      const cached = this.filterValuesCache.get(cacheKey);
      
      if (cached && (now - cached.cachedAt) < this.FILTER_CACHE_TTL) {
        console.log(`[StoreService] Using cached filter values for ${field} (${cached.values.length} values)`);
        return cached.values;
      }
    }
    
    const storesRef = collection(db, this.STORES_COLLECTION);
    const uniqueValues = new Set<string>();
    
    if (searchLower) {
      console.log(`[StoreService] Searching unique values for field: ${field} with search term: "${searchTerm}"`);
    } else {
      console.log(`[StoreService] Fetching ALL unique values for field: ${field}`);
    }
    const startTime = Date.now();
    
    try {
      // When searching, we need to fetch stores and filter client-side
      // Firestore doesn't support case-insensitive "contains" queries
      // Fetch in batches and filter for matching values
      const batchSize = 1000;
      const maxFetchForSearch = searchLower ? 5000 : 20000; // Limit fetch when searching
      let lastDoc: DocumentSnapshot | undefined = undefined;
      let totalFetched = 0;
      let batchNumber = 0;
      
      while (true) {
        batchNumber++;
        const constraints: QueryConstraint[] = [limit(batchSize)];
        
        if (lastDoc) {
          constraints.push(startAfter(lastDoc));
        }
        
        const q = query(storesRef, ...constraints);
        const querySnapshot = await getDocs(q);
        
        if (querySnapshot.empty) {
          console.log(`[StoreService] Batch ${batchNumber}: No more documents`);
          break;
        }
        
        querySnapshot.docs.forEach(docSnapshot => {
          const data = docSnapshot.data();
          const value = data[field];
          if (value !== null && value !== undefined && value !== '') {
            const valueStr = String(value).trim();
            
            // If searching, filter values that contain the search term (case-insensitive)
            if (searchLower) {
              if (valueStr.toLowerCase().includes(searchLower)) {
                uniqueValues.add(valueStr);
              }
            } else {
              uniqueValues.add(valueStr);
            }
          }
        });
        
        totalFetched += querySnapshot.docs.length;
        console.log(`[StoreService] Batch ${batchNumber}: Fetched ${querySnapshot.docs.length} docs, total: ${totalFetched}, unique matches: ${uniqueValues.size}`);
        
        // If we got fewer documents than requested, we've reached the end
        if (querySnapshot.docs.length < batchSize) {
          break;
        }
        
        // For search queries, stop early if we have enough results
        if (searchLower && uniqueValues.size >= maxValues) {
          console.log(`[StoreService] Found enough matching values (${uniqueValues.size}), stopping fetch`);
          break;
        }
        
        lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        
        // Safety limit - don't fetch more than maxFetchForSearch documents
        if (totalFetched >= maxFetchForSearch) {
          console.log(`[StoreService] Reached safety limit of ${maxFetchForSearch} documents`);
          break;
        }
      }
      
      if (searchLower) {
        console.log(`[StoreService] Found ${uniqueValues.size} unique matching values for "${searchTerm}" in field ${field} from ${totalFetched} documents in ${Date.now() - startTime}ms`);
      } else {
        console.log(`[StoreService] Got ${uniqueValues.size} unique values for ${field} from ${totalFetched} documents in ${Date.now() - startTime}ms`);
      }
      
      // Convert to sorted array
      const sortedValues = Array.from(uniqueValues).sort();
      
      // Limit to requested size
      const result = sortedValues.slice(0, maxValues);
      
      // Only cache when not searching (search results shouldn't be cached)
      if (!searchLower) {
        const cacheKey = `${field}_${maxValues}`;
        this.filterValuesCache.set(cacheKey, { values: result, cachedAt: now });
        console.log(`[StoreService] Cached ${result.length} filter values for ${field}`);
      }
      
      return result;
    } catch (error: any) {
      console.error(`[StoreService] Error fetching unique filter values for field ${field}:`, error);
      // Return empty array on error
      return [];
    }
  }

  /**
   * Get unique filter values for multiple fields in parallel
   * 
   * @param fields - Array of database field names
   * @param maxValues - Maximum number of unique values per field
   * @returns Promise resolving to object with field names as keys and arrays of values as values
   */
  static async getUniqueFilterValuesForFields(
    fields: string[], 
    maxValues: number = 1000
  ): Promise<Record<string, string[]>> {
    try {
      // Fetch all unique values in parallel
      const promises = fields.map(field => 
        this.getUniqueFilterValues(field, maxValues).then(values => ({ field, values }))
      );
      
      const results = await Promise.allSettled(promises);
      
      const uniqueValues: Record<string, string[]> = {};
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          uniqueValues[result.value.field] = result.value.values;
        } else {
          console.warn(`Failed to fetch unique values for field ${fields[index]}:`, result.reason);
          uniqueValues[fields[index]] = [];
        }
      });
      
      return uniqueValues;
    } catch (error: any) {
      console.error('Error fetching unique filter values for multiple fields:', error);
      return fields.reduce((acc, field) => {
        acc[field] = [];
        return acc;
      }, {} as Record<string, string[]>);
    }
  }

  /**
   * OPTIMIZED: Batch fetch notes data for multiple stores using collectionGroup query
   * This is much faster than querying each store individually
   * 
   * @param storeIds - Array of store IDs to fetch notes for
   * @returns Promise resolving to a map of storeId -> { count: number, lastDate: Date | Timestamp | null }
   * @private
   */
  /**
   * Get notes data for a single store with caching
   * 
   * @param storeId - Store ID
   * @returns Promise resolving to { count, lastDate }
   * @private
   */
  private static async getNotesDataCached(storeId: string): Promise<{ count: number; lastDate: Date | Timestamp | null }> {
    // Check cache first
    const cached = this.notesDataCache.get(storeId);
    const now = Date.now();
    
    if (cached && (now - cached.cachedAt) < this.NOTES_CACHE_TTL) {
      return { count: cached.count, lastDate: cached.lastDate };
    }
    
    // Fetch from database
    try {
      const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
      const notesSnapshot = await getDocs(query(notesRef, orderBy('createdAt', 'desc')));
      
      const count = notesSnapshot.size;
      const lastDate = notesSnapshot.docs[0]?.data().createdAt || null;
      
      // Cache result
      this.notesDataCache.set(storeId, { count, lastDate, cachedAt: now });
      
      return { count, lastDate };
    } catch (error) {
      console.warn(`[StoreService] Error fetching notes for store ${storeId}:`, error);
      return { count: 0, lastDate: null };
    }
  }

  /**
   * Batch get notes data for multiple stores with caching
   * Used to compute notesCount and lastVisit when denormalized fields are missing
   * 
   * @param storeIds - Array of store IDs
   * @returns Map of storeId -> { count, lastDate }
   */
  static async batchGetNotesData(storeIds: string[]): Promise<Map<string, { count: number; lastDate: Date | Timestamp | null }>> {
    const result = new Map<string, { count: number; lastDate: Date | Timestamp | null }>();
    
    // Initialize all stores with zero counts
    storeIds.forEach(storeId => {
      result.set(storeId, { count: 0, lastDate: null });
    });

    if (storeIds.length === 0) {
      return result;
    }

    // Check cache first for all stores
    const uncachedIds: string[] = [];
    const now = Date.now();
    
    for (const storeId of storeIds) {
      const cached = this.notesDataCache.get(storeId);
      if (cached && (now - cached.cachedAt) < this.NOTES_CACHE_TTL) {
        result.set(storeId, { count: cached.count, lastDate: cached.lastDate });
      } else {
        uncachedIds.push(storeId);
      }
    }
    
    // If all stores are cached, return immediately
    if (uncachedIds.length === 0) {
      console.log(`[StoreService] All ${storeIds.length} stores found in cache`);
      return result;
    }
    
    console.log(`[StoreService] ${uncachedIds.length} stores need fetching, ${storeIds.length - uncachedIds.length} from cache`);

    try {
      // Use collectionGroup to query all notes across all stores at once
      // This is much more efficient than querying each store individually
      const notesGroupRef = collectionGroup(db, 'notes');
      
      // Use collectionGroup query - fetch all notes
      // We need all notes to accurately compute counts and last visit dates
      // For performance, we'll process in memory after fetching
      const startTime = Date.now();
      let notesSnapshot;
      
      // Try to fetch all notes - if this is too slow, we can optimize later
      // For now, fetch all to ensure accuracy
      try {
        notesSnapshot = await getDocs(notesGroupRef);
        console.log(`[StoreService] Fetched ${notesSnapshot.size} notes from collectionGroup in ${Date.now() - startTime}ms`);
      } catch (error: any) {
        // If collectionGroup query fails (e.g., missing index), fallback to individual queries
        console.error('[StoreService] CollectionGroup query failed, falling back to individual store queries:', error);
        console.warn('[StoreService] This is slower but will ensure data accuracy');
        
        // Fallback: Query each store's notes individually
        const fallbackResults = new Map<string, { count: number; lastDate: Date | Timestamp | null }>();
        
        // Process in batches to avoid overwhelming Firestore
        const batchSize = 10;
        for (let i = 0; i < uncachedIds.length; i += batchSize) {
          const batch = uncachedIds.slice(i, i + batchSize);
          const batchPromises = batch.map(async (storeId) => {
            try {
              const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
              const notesQuery = query(notesRef, orderBy('createdAt', 'desc'));
              const notesSnapshot = await getDocs(notesQuery);
              
              const count = notesSnapshot.size;
              let lastDate: Date | Timestamp | null = null;
              
              if (notesSnapshot.size > 0) {
                const latestNote = notesSnapshot.docs[0].data();
                lastDate = latestNote.createdAt || null;
              }
              
              return { storeId, count, lastDate };
            } catch (err) {
              console.warn(`[StoreService] Error fetching notes for store ${storeId}:`, err);
              return { storeId, count: 0, lastDate: null };
            }
          });
          
          const batchResults = await Promise.all(batchPromises);
          batchResults.forEach(({ storeId, count, lastDate }) => {
            fallbackResults.set(storeId, { count, lastDate });
            // Cache the result
            this.notesDataCache.set(storeId, { count, lastDate, cachedAt: now });
          });
        }
        
        // Merge fallback results with cached results
        fallbackResults.forEach((data, storeId) => {
          result.set(storeId, data);
        });
        
        console.log(`[StoreService] Fallback: Fetched notes for ${fallbackResults.size} stores`);
        return result;
      }

      // Process notes and group by store ID (only for uncached stores)
      const storeIdsSet = new Set(uncachedIds);
      const storeNotesMap = new Map<string, Array<{ createdAt: Date | Timestamp }>>();
      
      console.log(`[StoreService] Processing ${notesSnapshot.size} notes for ${uncachedIds.length} uncached stores`);
      console.log(`[StoreService] Looking for stores:`, uncachedIds.slice(0, 5), uncachedIds.length > 5 ? `... and ${uncachedIds.length - 5} more` : '');

      let processedNotesCount = 0;
      let skippedNotesCount = 0;
      let notesForOtherStores = 0;
      
      notesSnapshot.docs.forEach(noteDoc => {
        // Extract storeId from the document path: Stores/{storeId}/notes/{noteId}
        const pathParts = noteDoc.ref.path.split('/');
        
        // Path should be: Stores/{storeId}/notes/{noteId}
        // So we need at least 4 parts: ['Stores', storeId, 'notes', noteId]
        let storeId: string | null = null;
        
        if (pathParts.length >= 4 && pathParts[0] === 'Stores' && pathParts[2] === 'notes') {
          storeId = pathParts[1];
        } else if (pathParts.length >= 2 && pathParts[0] === 'Stores') {
          // Fallback: if path is Stores/{storeId}/notes/{noteId} but we're not sure about structure
          // Try to find the storeId by looking for it in the path
          const storesIndex = pathParts.indexOf('Stores');
          if (storesIndex >= 0 && pathParts.length > storesIndex + 1) {
            storeId = pathParts[storesIndex + 1];
          }
        }
        
        if (!storeId) {
          skippedNotesCount++;
          if (skippedNotesCount <= 3) {
            console.warn(`[StoreService] Could not extract storeId from note path: ${noteDoc.ref.path}`);
          }
          return;
        }
        
        // Only process notes for stores we care about
        if (storeIdsSet.has(storeId)) {
          // Debug: Log first few notes being processed
          if (processedNotesCount < 3) {
            console.log(`[StoreService] Processing note ${noteDoc.id} for store ${storeId}, path: ${noteDoc.ref.path}`);
          }
          const noteData = noteDoc.data();
          
          // Get createdAt timestamp - this is the primary field for visit dates
          // createdAt is a Firestore Timestamp, which is what we need for sorting
          // If createdAt exists, it means there's a visit
          let createdAt = noteData.createdAt;
          
          // If createdAt doesn't exist, try to use the date field as fallback
          if (!createdAt && noteData.date) {
            // Try to parse the date string (format: "12/9/2025" or similar)
            try {
              const dateStr = noteData.date;
              // Handle MM/DD/YYYY format
              const dateParts = dateStr.split('/');
              if (dateParts.length === 3) {
                const month = parseInt(dateParts[0]) - 1; // Month is 0-indexed
                const day = parseInt(dateParts[1]);
                const year = parseInt(dateParts[2]);
                createdAt = Timestamp.fromDate(new Date(year, month, day));
              } else {
                // Try parsing as ISO string or other format
                createdAt = Timestamp.fromDate(new Date(dateStr));
              }
            } catch (e) {
              // Ignore parse errors - we'll count it anyway
            }
          }
          
          if (!storeNotesMap.has(storeId)) {
            storeNotesMap.set(storeId, []);
          }
          
          // IMPORTANT: Count ALL notes as visits - the presence of a note document means there's a visit
          // Use createdAt if available (primary field for sorting), otherwise use a fallback
          if (createdAt) {
            storeNotesMap.get(storeId)!.push({ createdAt });
            processedNotesCount++;
          } else {
            // Still count it as a visit even without date - the note document itself proves there was a visit
            // Use a very old timestamp so it sorts to the bottom, but still counts as a visit
            storeNotesMap.get(storeId)!.push({ createdAt: Timestamp.fromDate(new Date(0)) });
            processedNotesCount++;
            console.warn(`[StoreService] Note ${noteDoc.id} for store ${storeId} has no createdAt - counting as visit with fallback date`);
          }
        } else {
          // Note belongs to a store we're not currently fetching
          notesForOtherStores++;
        }
      });
      
      console.log(`[StoreService] Processed ${processedNotesCount} notes for target stores, ${notesForOtherStores} notes for other stores, ${skippedNotesCount} notes with invalid paths`);
      
      // Debug: Log which stores got notes
      if (storeNotesMap.size > 0) {
        console.log(`[StoreService] Found notes for ${storeNotesMap.size} stores:`, Array.from(storeNotesMap.keys()).slice(0, 10));
      } else {
        console.warn(`[StoreService] ⚠️ No notes found for any of the ${uncachedIds.length} stores being fetched!`);
        console.warn(`[StoreService] This might indicate: 1) Collection group query issue, 2) Path extraction problem, or 3) Stores actually have no notes`);
      }

      // Calculate counts and last dates for each store
      storeNotesMap.forEach((notes, storeId) => {
        const count = notes.length;
        let lastDate: Date | Timestamp | null = null;

        if (notes.length > 0) {
          // Find the most recent date using createdAt timestamps
          // createdAt is a Firestore Timestamp, which we'll use directly for sorting
          const timestamps = notes.map(n => {
            const d = n.createdAt;
            
            // Handle Firestore Timestamp (most common case)
            if (d && typeof d === 'object' && 'toDate' in d && typeof d.toDate === 'function') {
              return d as Timestamp; // Keep as Timestamp for proper sorting
            }
            
            // Handle Date objects
            if (d instanceof Date) {
              return Timestamp.fromDate(d);
            }
            
            // Handle string dates
            if (typeof d === 'string') {
              return Timestamp.fromDate(new Date(d));
            }
            
            // Fallback - try to convert to Timestamp
            try {
              return Timestamp.fromDate(new Date(d as any));
            } catch (e) {
              console.warn(`[StoreService] Failed to convert createdAt to Timestamp for store ${storeId}:`, d);
              return Timestamp.now(); // Use current time as fallback
            }
          });
          
          // Find the most recent timestamp
          lastDate = timestamps.reduce((latest, current) => {
            // Compare timestamps properly using milliseconds
            const latestTime = latest instanceof Timestamp ? latest.toMillis() : (latest instanceof Date ? latest.getTime() : 0);
            const currentTime = current instanceof Timestamp ? current.toMillis() : (current instanceof Date ? current.getTime() : 0);
            return currentTime > latestTime ? current : latest;
          }, timestamps[0]);
          
          console.log(`[StoreService] Store ${storeId}: ${count} visits, last visit: ${lastDate instanceof Timestamp ? lastDate.toDate() : lastDate}`);
        }

        result.set(storeId, { count, lastDate });
        
        // Cache the result
        this.notesDataCache.set(storeId, { count, lastDate, cachedAt: now });
      });

      const storesWithVisits = Array.from(result.values()).filter(r => r.count > 0).length;
      console.log(`[StoreService] ✓ Processed notes for ${uncachedIds.length} uncached stores (${storesWithVisits} with visits), ${storeIds.length - uncachedIds.length} from cache`);
      console.log(`[StoreService] Total notes processed: ${processedNotesCount}, notes in map: ${Array.from(storeNotesMap.values()).reduce((sum, notes) => sum + notes.length, 0)}`);
      
      // Debug: Log sample of stores with visits
      if (storesWithVisits > 0) {
        const sampleEntries = Array.from(result.entries())
          .filter(([_, data]) => data.count > 0)
          .slice(0, 5);
        console.log('[StoreService] Sample stores with visits:', sampleEntries.map(([storeId, data]) => ({
          storeId,
          count: data.count,
          lastDate: data.lastDate ? (data.lastDate instanceof Timestamp ? data.lastDate.toDate().toISOString() : String(data.lastDate)) : null
        })));
      } else {
        console.warn('[StoreService] ⚠️ No stores with visits found in notes data.');
        console.warn('[StoreService] Debug info:', {
          totalNotesFetched: notesSnapshot.size,
          totalStores: storeIds.length,
          processedNotes: processedNotesCount,
          skippedNotes: skippedNotesCount,
          notesInMap: Array.from(storeNotesMap.values()).reduce((sum, notes) => sum + notes.length, 0),
          sampleNotePaths: notesSnapshot.docs.slice(0, 5).map(doc => ({
            path: doc.ref.path,
            hasCreatedAt: !!doc.data().createdAt,
            createdAt: doc.data().createdAt ? (doc.data().createdAt.toDate ? doc.data().createdAt.toDate().toISOString() : String(doc.data().createdAt)) : 'missing'
          })),
          sampleStoreIds: storeIds.slice(0, 5)
        });
      }
    } catch (error: any) {
      console.error('[StoreService] Error batch fetching notes:', error);
      // Return empty results on error
    }

    return result;
  }

  /**
   * Get notes count for a specific store
   * Returns the actual count of notes/visits for the store
   * 
   * @param storeId - The store ID
   * @returns Promise resolving to the actual count of notes
   * @private
   */
  private static async getNotesCount(storeId: string): Promise<number> {
    try {
      const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
      // Fetch all notes to get accurate count
      // Note: For very large collections, consider denormalizing notesCount into the store document
      const querySnapshot = await getDocs(notesRef);
      const count = querySnapshot.size;
      if (count > 0) {
        console.log(`[StoreService] Store ${storeId} has ${count} visits`);
      }
      return count;
    } catch (error: any) {
      console.warn(`Failed to count notes for store ${storeId}:`, error);
      return 0;
    }
  }

  /**
   * Get the most recent note date for a specific store
   * 
   * @param storeId - The store ID
   * @returns Promise resolving to the most recent note date (Date | Timestamp) or null
   * @private
   */
  private static async getMostRecentNoteDate(storeId: string): Promise<Date | Timestamp | null> {
    try {
      const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
      const notesQuery = query(notesRef, orderBy('createdAt', 'desc'), limit(1));
      const notesSnapshot = await getDocs(notesQuery);
      
      if (!notesSnapshot.empty) {
        const latestNote = notesSnapshot.docs[0].data();
        const createdAt = latestNote.createdAt;
        
        // Convert Timestamp to Date if needed
        if (createdAt) {
          return createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
        }
      }
      
      return null;
    } catch (error: any) {
      console.warn(`Failed to get most recent note date for store ${storeId}:`, error);
      return null;
    }
  }

  /**
   * Log store history entry
   * 
   * @param entry - History entry data
   * @private
   */
  private static async logStoreHistory(entry: Omit<StoreHistoryEntry, 'id'>): Promise<void> {
    try {
      const historyRef = collection(db, this.STORE_HISTORY_COLLECTION);
      await addDoc(historyRef, {
        ...entry,
        timestamp: Timestamp.now()
      });
    } catch (error: any) {
      console.warn('Failed to log store history:', error.message);
      // Don't throw - history logging is non-critical
    }
  }

  /**
   * Generate a unique customer number
   * 
   * @private
   * @returns Promise resolving to new customer number
   */
  private static async generateCustomerNumber(): Promise<string> {
    try {
      // Get all stores to find the highest customer number
      const storesRef = collection(db, this.STORES_COLLECTION);
      const querySnapshot = await getDocs(query(storesRef, orderBy('CustomerNo', 'desc'), limit(1)));

      if (querySnapshot.empty) {
        return '10001'; // Start from 10001
      }

      const lastCustomerNo = querySnapshot.docs[0].data().CustomerNo || querySnapshot.docs[0].data().customerNo;
      const nextNumber = parseInt(lastCustomerNo.toString()) + 1;
      
      return nextNumber.toString();
    } catch (error: any) {
      console.error('Error generating customer number:', error);
      // Fallback to timestamp-based number
      return `1${Date.now().toString().slice(-5)}`;
    }
  }

  /**
   * Filter stores by notes criteria
   * Fetches notes for each store and filters based on the criteria
   * 
   * @param storeIds - Array of store IDs to filter
   * @param criteria - Notes filter criteria
   * @returns Promise resolving to array of store IDs that match the criteria
   */
  /**
   * OPTIMIZED: Filter stores by notes criteria using collection group query
   * This is 100x faster than the previous N+1 query approach
   * 
   * @param storeIds - Array of store IDs to filter (optional - if empty, checks all stores)
   * @param criteria - Filter criteria
   * @param salesRepMap - Map of sales rep IDs to their info
   * @returns Promise resolving to array of matching store IDs
   */
  static async filterStoresByNotes(
    storeIds: string[],
    criteria: {
      competitors?: string[];
      storeLevelRange?: { min: number; max: number } | null;
      lowStockCategories?: string[];
      needsManagerContact?: boolean | null;
      hasNotes?: boolean | null;
      visitTypes?: string[];
      visitDateRange?: { from: string; to: string } | null;
      salesRepIds?: string[];
    },
    salesRepMap?: Map<string, { email?: string; name?: string }>
  ): Promise<string[]> {
    // Check if any criteria is active
    const hasActiveCriteria = 
      (criteria.competitors && criteria.competitors.length > 0) ||
      criteria.storeLevelRange !== null ||
      (criteria.lowStockCategories && criteria.lowStockCategories.length > 0) ||
      criteria.needsManagerContact !== null ||
      criteria.hasNotes !== null ||
      (criteria.visitTypes && criteria.visitTypes.length > 0) ||
      (criteria.visitDateRange !== null) ||
      (criteria.salesRepIds && criteria.salesRepIds.length > 0);

    if (!hasActiveCriteria) {
      return storeIds.length > 0 ? storeIds : [];
    }

        console.log('[StoreService] Filtering stores by notes criteria (OPTIMIZED):', JSON.stringify(criteria, null, 2));
    console.log('[StoreService] Date range filter:', {
      from: criteria.visitDateRange?.from,
      to: criteria.visitDateRange?.to,
      hasRange: !!criteria.visitDateRange
    });
    const startTime = Date.now();

    try {
      // OPTIMIZATION: Use collection group query to fetch all notes at once
      // This is much faster than querying each store individually (N+1 problem)
      const notesGroupRef = collectionGroup(db, 'notes');
      const constraints: QueryConstraint[] = [];

      // Apply database-level filters where possible
      // Note: Some filters must be done client-side (e.g., competitors array matching)
      
      // Filter by store level range (if specified)
      if (criteria.storeLevelRange) {
        constraints.push(where('storeLevel', '>=', criteria.storeLevelRange.min));
        constraints.push(where('storeLevel', '<=', criteria.storeLevelRange.max));
      }

      // Filter by visit date range (if specified)
      if (criteria.visitDateRange) {
        if (criteria.visitDateRange.from) {
          // Parse date string (YYYY-MM-DD) and set to start of day
          const fromDateStr = criteria.visitDateRange.from;
          const fromDate = new Date(fromDateStr + 'T00:00:00.000Z');
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(fromDate)));
        }
        if (criteria.visitDateRange.to) {
          // Parse date string (YYYY-MM-DD) and set to end of day
          const toDateStr = criteria.visitDateRange.to;
          const toDate = new Date(toDateStr + 'T23:59:59.999Z');
          constraints.push(where('createdAt', '<=', Timestamp.fromDate(toDate)));
        }
      }

      // Filter by visit types (if specified)
      if (criteria.visitTypes && criteria.visitTypes.length > 0) {
        // Firestore 'in' operator supports up to 10 values
        const visitTypesToQuery = criteria.visitTypes.slice(0, 10);
        constraints.push(where('visitType', 'in', visitTypesToQuery));
      }

      // Filter by manager contact (if specified)
      if (criteria.needsManagerContact !== null) {
        constraints.push(where('requestManagerContact', '==', criteria.needsManagerContact));
      }

      // Build query
      const q = constraints.length > 0 
        ? query(notesGroupRef, ...constraints)
        : query(notesGroupRef);

      console.log(`[StoreService] Fetching notes with ${constraints.length} database filters...`);
      console.log(`[StoreService] Query constraints:`, constraints.map(c => {
        // Log constraint details for debugging
        if (c.type === 'where') {
          return `${c.fieldPath} ${c.op} ${c.value}`;
        }
        return String(c);
      }));
      
      let notesSnapshot;
      try {
        notesSnapshot = await getDocs(q);
        console.log(`[StoreService] ✓ Fetched ${notesSnapshot.size} notes in ${Date.now() - startTime}ms`);
      } catch (queryError: any) {
        // If query fails (e.g., missing index), try fallback strategies
        console.error('[StoreService] Collection group query failed:', queryError);
        
        // Check if it's an index error
        const isIndexError = queryError?.code === 'failed-precondition' || 
                            queryError?.message?.includes('index') ||
                            queryError?.message?.includes('requires an index');
        
        if (isIndexError) {
          // Extract project ID from error message or use default
          const projectIdMatch = queryError?.message?.match(/project\/([^\/]+)/);
          const projectId = projectIdMatch?.[1] || 'mckenzieportal-41c3d';
          const indexUrl = queryError?.message?.match(/https:\/\/[^\s]+/)?.[0] || 
                          `https://console.firebase.google.com/project/${projectId}/firestore/indexes`;
          const consoleUrl = `https://console.firebase.google.com/project/${projectId}/firestore/indexes`;
          
          console.error(`%c[StoreService] ⚠️ MISSING FIREBASE INDEX DETECTED!`, 'color: red; font-weight: bold; font-size: 14px;');
          console.error(`%c[StoreService] The query requires a Firestore index to be created.`, 'color: orange; font-weight: bold;');
          console.error(`%c[StoreService] 🔗 Click this link to create the index automatically:`, 'color: blue; font-weight: bold;');
          if (indexUrl && indexUrl.includes('create_composite')) {
            console.error(`%c${indexUrl}`, 'color: blue; text-decoration: underline; font-size: 12px;');
            console.error(`%c[StoreService] OR manually create the index at:`, 'color: blue;');
          }
          console.error(`%c${consoleUrl}`, 'color: blue; text-decoration: underline; font-size: 12px;');
          console.error(`%c[StoreService] Error details:`, 'color: gray;');
          console.error(queryError);
          
          // Also show a user-friendly error that can be caught by UI
          throw new Error(`Missing Firestore index. Please create it at: ${indexUrl || consoleUrl}`);
        }
        
        console.warn('[StoreService] Attempting fallback: fetching all notes and filtering client-side...');
        
        // Fallback Strategy 1: Try without problematic filters
        // Remove filters that might need indexes, keep simple ones
        const fallbackConstraints = constraints.filter(c => {
          // Keep equality filters (they don't need special indexes)
          if (c.type === 'where' && c.op === '==') {
            return true;
          }
          // Remove range filters and 'in' operators (they need indexes)
          if (c.type === 'where' && (c.op === '>=' || c.op === '<=' || c.op === 'in')) {
            return false;
          }
          return true;
        });
        
        const fallbackQ = fallbackConstraints.length > 0
          ? query(notesGroupRef, ...fallbackConstraints)
          : query(notesGroupRef);
        
        try {
          notesSnapshot = await getDocs(fallbackQ);
          console.log(`[StoreService] ✓ Fallback: Fetched ${notesSnapshot.size} notes (will filter range/in queries client-side)`);
        } catch (fallbackError: any) {
          console.error('[StoreService] Fallback query also failed:', fallbackError);
          // Last resort: fetch all notes and filter everything client-side
          try {
            notesSnapshot = await getDocs(query(notesGroupRef));
            console.log(`[StoreService] ✓ Last resort: Fetched ${notesSnapshot.size} notes (will filter ALL criteria client-side)`);
            console.warn(`[StoreService] ⚠️ This is slower but ensures filters work even without indexes.`);
          } catch (lastResortError: any) {
            console.error('[StoreService] ❌ All query attempts failed:', lastResortError);
            throw new Error(`Failed to fetch notes: ${lastResortError.message}. Please check Firestore permissions and indexes.`);
          }
        }
      }

      // Process notes to find matching stores
      const storeIdsSet = storeIds.length > 0 ? new Set(storeIds) : null;
      const storeNotesMap = new Map<string, any[]>();

      // Group notes by store ID
      notesSnapshot.docs.forEach(noteDoc => {
        // Extract storeId from path: Stores/{storeId}/notes/{noteId}
        const pathParts = noteDoc.ref.path.split('/');
        let storeId: string | null = null;

        if (pathParts.length >= 4 && pathParts[0] === 'Stores' && pathParts[2] === 'notes') {
          storeId = pathParts[1];
        }

        if (!storeId) return;

        // If filtering by specific store IDs, skip stores not in the list
        if (storeIdsSet && !storeIdsSet.has(storeId)) return;

        if (!storeNotesMap.has(storeId)) {
          storeNotesMap.set(storeId, []);
        }
        storeNotesMap.get(storeId)!.push(noteDoc.data());
      });

      console.log(`[StoreService] Found notes for ${storeNotesMap.size} stores`);
      
      // Debug: Log sample of notes found
      if (storeNotesMap.size > 0) {
        const sampleStore = Array.from(storeNotesMap.entries())[0];
        const sampleNotes = sampleStore[1];
        console.log(`[StoreService] Sample store ${sampleStore[0]} has ${sampleNotes.length} notes:`, {
          firstNote: {
            createdAt: sampleNotes[0]?.createdAt,
            date: sampleNotes[0]?.date,
            visitType: sampleNotes[0]?.visitType
          }
        });
      }

      // Filter stores based on criteria (client-side for complex matching)
      const matchingStoreIds: string[] = [];
      
      // If hasNotes filter is set, we need to check all stores (not just those with notes)
      if (criteria.hasNotes === false) {
        // Need to check stores without notes
        // For this, we'd need to query all stores, but for now, we'll only return stores that have notes
        // This is a limitation - to properly handle "hasNotes: false", we'd need to query all stores
        // For performance, we'll skip this edge case for now
      }

      // Process each store's notes
      for (const [storeId, notes] of storeNotesMap.entries()) {
        // Check hasNotes filter
        if (criteria.hasNotes !== null) {
          if (criteria.hasNotes && notes.length === 0) continue;
          if (!criteria.hasNotes && notes.length > 0) continue;
        }

        // If no notes and we need note-specific criteria, skip
        if (notes.length === 0) {
          // If any note-specific criteria is set, this store doesn't match
          if (
            (criteria.competitors && criteria.competitors.length > 0) ||
            criteria.storeLevelRange ||
            (criteria.lowStockCategories && criteria.lowStockCategories.length > 0) ||
            criteria.needsManagerContact === true ||
            (criteria.visitTypes && criteria.visitTypes.length > 0) ||
            criteria.visitDateRange !== null ||
            (criteria.salesRepIds && criteria.salesRepIds.length > 0)
          ) {
            continue;
          }
          matchingStoreIds.push(storeId);
          continue;
        }

        // Check if ANY note matches ALL active criteria
        let matchesAllCriteria = false;

        for (const note of notes) {
          let matchesCompetitors = !criteria.competitors || criteria.competitors.length === 0;
          let matchesStoreLevel = !criteria.storeLevelRange;
          let matchesLowStock = !criteria.lowStockCategories || criteria.lowStockCategories.length === 0;
          let matchesManagerContact = criteria.needsManagerContact === null;
          let matchesVisitTypes = !criteria.visitTypes || criteria.visitTypes.length === 0;
          let matchesVisitDateRange = !criteria.visitDateRange;
          let matchesSalesRep = !criteria.salesRepIds || criteria.salesRepIds.length === 0;

          // Check competitors (client-side - complex array matching)
          if (criteria.competitors && criteria.competitors.length > 0) {
            const noteCompetitors = note.competitors || [];
            if (Array.isArray(noteCompetitors) && noteCompetitors.length > 0) {
              const noteCompetitorsLower = noteCompetitors.map((c: any) => String(c || '').toLowerCase().trim()).filter(c => c);
              const criteriaCompetitorsLower = criteria.competitors.map(c => String(c || '').toLowerCase().trim()).filter(c => c);
              
              matchesCompetitors = criteriaCompetitorsLower.some(c => {
                return noteCompetitorsLower.some(nc => {
                  if (nc === c) return true;
                  if (nc.includes(c) || c.includes(nc)) return true;
                  if (c === 'others' && nc !== 'mckenzie seeds is exclusive' && nc !== 'mckenzie') return true;
                  return false;
                });
              });
            }
          }

          // Check store level (already filtered at DB level, but verify)
          if (criteria.storeLevelRange) {
            const storeLevel = note.storeLevel;
            if (storeLevel !== undefined && storeLevel !== null) {
              const storeLevelNum = typeof storeLevel === 'string' ? parseFloat(storeLevel) : Number(storeLevel);
              if (!isNaN(storeLevelNum)) {
                matchesStoreLevel = storeLevelNum >= criteria.storeLevelRange.min && storeLevelNum <= criteria.storeLevelRange.max;
              }
            }
          }

          // Check low stock categories (client-side)
          if (criteria.lowStockCategories && criteria.lowStockCategories.length > 0) {
            const noteLowStock = note.lowStockCategories || [];
            const noteLowStockLower = noteLowStock.map((c: string) => String(c).toLowerCase().trim());
            const criteriaLowStockLower = criteria.lowStockCategories.map(c => String(c).toLowerCase().trim());
            matchesLowStock = criteriaLowStockLower.some(c => noteLowStockLower.includes(c));
          }

          // Check manager contact (already filtered at DB level, but verify)
          if (criteria.needsManagerContact !== null) {
            const noteRequestContact = note.requestManagerContact === true || note.requestManagerContact === 'true';
            matchesManagerContact = criteria.needsManagerContact === noteRequestContact;
          }

          // Check visit types (already filtered at DB level, but verify)
          if (criteria.visitTypes && criteria.visitTypes.length > 0) {
            const noteVisitType = note.visitType;
            if (noteVisitType) {
              const noteVisitTypeLower = String(noteVisitType).toLowerCase().trim();
              const criteriaVisitTypesLower = criteria.visitTypes.map(vt => String(vt).toLowerCase().trim());
              matchesVisitTypes = criteriaVisitTypesLower.includes(noteVisitTypeLower);
            }
          }

          // Check visit date range (filter client-side if DB filter failed)
          if (criteria.visitDateRange) {
            let noteDate: any = note.createdAt || note.date;
            if (noteDate) {
              let noteDateValue: Date;
              
              // Handle Firestore Timestamp
              if (noteDate.toDate && typeof noteDate.toDate === 'function') {
                noteDateValue = noteDate.toDate();
              } else if (noteDate instanceof Date) {
                noteDateValue = noteDate;
              } else if (noteDate && typeof noteDate === 'object' && 'seconds' in noteDate) {
                // Firestore Timestamp object with seconds property
                noteDateValue = new Date(noteDate.seconds * 1000);
              } else if (typeof noteDate === 'string') {
                // Try parsing date string
                noteDateValue = new Date(noteDate);
              } else {
                noteDateValue = new Date(noteDate);
              }
              
              if (!isNaN(noteDateValue.getTime())) {
                const noteDateStr = noteDateValue.toISOString().split('T')[0]; // YYYY-MM-DD
                const fromDate = criteria.visitDateRange.from || '';
                const toDate = criteria.visitDateRange.to || '';
                
                // Compare dates as strings (YYYY-MM-DD format)
                // Both dates should be in YYYY-MM-DD format for string comparison
                const dateInRange = (!fromDate || noteDateStr >= fromDate) && (!toDate || noteDateStr <= toDate);
                
                if (dateInRange) {
                  matchesVisitDateRange = true;
                } else {
                  // Debug: Log why date didn't match (only for first few)
                  if (matchingStoreIds.length < 3) {
                    console.log(`[StoreService] Date filter mismatch: noteDate=${noteDateStr}, from=${fromDate}, to=${toDate}, inRange=${dateInRange}`);
                  }
                }
              } else {
                // Invalid date - if date range is required, this note doesn't match
                if (criteria.visitDateRange.from || criteria.visitDateRange.to) {
                  matchesVisitDateRange = false;
                  if (matchingStoreIds.length < 3) {
                    console.warn(`[StoreService] Invalid date in note:`, noteDate);
                  }
                }
              }
            } else {
              // No date field - if date range is required, this note doesn't match
              if (criteria.visitDateRange.from || criteria.visitDateRange.to) {
                matchesVisitDateRange = false;
              }
            }
          }

          // Check sales rep (client-side)
          if (criteria.salesRepIds && criteria.salesRepIds.length > 0 && salesRepMap) {
            const noteAuthor = note.author || '';
            const noteSalesRepName = note.salesRepName || '';
            
            matchesSalesRep = criteria.salesRepIds.some(repId => {
              const repInfo = salesRepMap.get(repId);
              if (!repInfo) return false;
              const authorMatches = repInfo.email && noteAuthor.toLowerCase() === repInfo.email.toLowerCase();
              const nameMatches = repInfo.name && noteSalesRepName.toLowerCase().includes(repInfo.name.toLowerCase());
              return authorMatches || nameMatches;
            });
          }

          // If this note matches all criteria, the store matches
          if (matchesCompetitors && matchesStoreLevel && matchesLowStock && matchesManagerContact && 
              matchesVisitTypes && matchesVisitDateRange && matchesSalesRep) {
            matchesAllCriteria = true;
            break; // Found a matching note, no need to check others
          }
        }

        if (matchesAllCriteria) {
          matchingStoreIds.push(storeId);
        }
      }

      // If filtering by specific store IDs and hasNotes is false, include stores without notes
      if (criteria.hasNotes === false && storeIdsSet) {
        for (const storeId of storeIdsSet) {
          if (!storeNotesMap.has(storeId)) {
            matchingStoreIds.push(storeId);
          }
        }
      }

      console.log(`[StoreService] ✓ Filtered to ${matchingStoreIds.length} matching stores in ${Date.now() - startTime}ms (${notesSnapshot.size} notes processed)`);
      
      // Debug: Log why stores matched or didn't match
      if (matchingStoreIds.length === 0 && notesSnapshot.size > 0) {
        console.warn('[StoreService] ⚠️ No stores matched despite having notes. This might indicate:');
        console.warn('  1. Date range filter is too narrow');
        console.warn('  2. Other filters are excluding all stores');
        console.warn('  3. Notes don\'t match the filter criteria');
        console.warn(`[StoreService] Total notes processed: ${notesSnapshot.size}, Stores with notes: ${storeNotesMap.size}`);
      }
      
      return matchingStoreIds;
    } catch (error: any) {
      console.error('[StoreService] Error filtering stores by notes:', error);
      // Fallback to empty array on error
      return [];
    }
  }

  /**
   * @deprecated This method is kept for backward compatibility but uses the optimized version
   * The old N+1 query approach has been replaced with collection group queries
   */
  static async filterStoresByNotes_OLD(
    storeIds: string[],
    criteria: {
      competitors?: string[];
      storeLevelRange?: { min: number; max: number } | null;
      lowStockCategories?: string[];
      needsManagerContact?: boolean | null;
      hasNotes?: boolean | null;
      visitTypes?: string[];
      visitDateRange?: { from: string; to: string } | null;
      salesRepIds?: string[];
    },
    salesRepMap?: Map<string, { email?: string; name?: string }>
  ): Promise<string[]> {
    // Use optimized version
    return this.filterStoresByNotes(storeIds, criteria, salesRepMap);
  }

  /**
   * Legacy method - redirects to optimized version
   * @deprecated
   */
  static async filterStoresByNotes_LEGACY(
    storeIds: string[],
    criteria: any,
    salesRepMap?: Map<string, { email?: string; name?: string }>
  ): Promise<string[]> {
    // Old implementation - kept for reference but not used
    if (storeIds.length === 0) {
      return [];
    }

    // Check if any criteria is active
    const hasActiveCriteria = 
      (criteria.competitors && criteria.competitors.length > 0) ||
      criteria.storeLevelRange !== null ||
      (criteria.lowStockCategories && criteria.lowStockCategories.length > 0) ||
      criteria.needsManagerContact !== null ||
      criteria.hasNotes !== null ||
      (criteria.visitTypes && criteria.visitTypes.length > 0) ||
      (criteria.visitDateRange !== null) ||
      (criteria.salesRepIds && criteria.salesRepIds.length > 0);

    if (!hasActiveCriteria) {
      return storeIds;
    }

    console.log('[StoreService] Filtering stores by notes criteria:', JSON.stringify(criteria, null, 2));
    const startTime = Date.now();

    const matchingStoreIds: string[] = [];

    // Process stores in batches to avoid overwhelming Firestore
    // Increased batch size for better performance
    const batchSize = 50;
    let processedCount = 0;
    
    for (let i = 0; i < storeIds.length; i += batchSize) {
      const batch = storeIds.slice(i, i + batchSize);
      processedCount += batch.length;
      console.log(`[StoreService] Processing batch ${Math.floor(i / batchSize) + 1}/${Math.ceil(storeIds.length / batchSize)} (${processedCount}/${storeIds.length} stores)`);
      
      // Fetch notes for each store in the batch in parallel
      const batchPromises = batch.map(async (storeId) => {
        try {
          const notesRef = collection(db, this.STORES_COLLECTION, storeId, 'notes');
          const notesSnapshot = await getDocs(notesRef);
          
          const notes = notesSnapshot.docs.map(doc => doc.data());
          
          // Check hasNotes filter
          if (criteria.hasNotes !== null) {
            if (criteria.hasNotes && notes.length === 0) {
              return null; // Store has no notes, but filter requires notes
            }
            if (!criteria.hasNotes && notes.length > 0) {
              return null; // Store has notes, but filter requires no notes
            }
          }

          // If no notes and we need to check note-specific criteria, skip
          if (notes.length === 0) {
            // If any note-specific criteria is set, this store doesn't match
            if (
              (criteria.competitors && criteria.competitors.length > 0) ||
              criteria.storeLevelRange ||
              (criteria.lowStockCategories && criteria.lowStockCategories.length > 0) ||
              criteria.needsManagerContact === true ||
              (criteria.visitTypes && criteria.visitTypes.length > 0) ||
              criteria.visitDateRange !== null ||
              (criteria.salesRepIds && criteria.salesRepIds.length > 0)
            ) {
              return null;
            }
            return storeId; // No notes, but no note-specific criteria either
          }

          // Initialize match flags - start as false if criteria is active, true if no criteria
          // A store matches if ANY note matches ALL active criteria
          let matchesCompetitors = !criteria.competitors || criteria.competitors.length === 0;
          let matchesStoreLevel = !criteria.storeLevelRange;
          let matchesLowStock = !criteria.lowStockCategories || criteria.lowStockCategories.length === 0;
          let matchesManagerContact = criteria.needsManagerContact === null;
          let matchesVisitTypes = !criteria.visitTypes || criteria.visitTypes.length === 0;
          let matchesVisitDateRange = !criteria.visitDateRange;
          let matchesSalesRep = !criteria.salesRepIds || criteria.salesRepIds.length === 0;

          for (const note of notes) {
            // Check competitors - normalize for case-insensitive matching
            if (criteria.competitors && criteria.competitors.length > 0 && !matchesCompetitors) {
              const noteCompetitors = note.competitors || [];
              if (Array.isArray(noteCompetitors) && noteCompetitors.length > 0) {
                // Convert to lowercase for case-insensitive comparison
                const noteCompetitorsLower = noteCompetitors.map((c: any) => String(c || '').toLowerCase().trim()).filter(c => c);
                const criteriaCompetitorsLower = criteria.competitors.map(c => String(c || '').toLowerCase().trim()).filter(c => c);
                
                // Check if any competitor matches (case-insensitive, exact or partial)
                if (criteriaCompetitorsLower.length > 0 && noteCompetitorsLower.length > 0) {
                  const hasMatch = criteriaCompetitorsLower.some(c => {
                    return noteCompetitorsLower.some(nc => {
                      // Exact match (most common case)
                      if (nc === c) {
                        console.log(`[StoreService] Exact competitor match: "${nc}" === "${c}"`);
                        return true;
                      }
                      // Partial match (one contains the other) - be more lenient
                      if (nc.includes(c) || c.includes(nc)) {
                        console.log(`[StoreService] Partial competitor match: "${nc}" contains "${c}" or vice versa`);
                        return true;
                      }
                      // Check for "others" special case
                      if (c === 'others' && nc !== 'mckenzie seeds is exclusive' && nc !== 'mckenzie') {
                        console.log(`[StoreService] "Others" competitor match: "${nc}"`);
                        return true;
                      }
                      return false;
                    });
                  });
                  
                  if (hasMatch) {
                    matchesCompetitors = true;
                    console.log(`[StoreService] Store ${storeId} matches competitor filter:`, {
                      criteria: criteria.competitors,
                      noteCompetitors: note.competitors
                    });
                  }
                }
              }
            }

            // Check store level - handle both string and number types
            if (criteria.storeLevelRange && !matchesStoreLevel) {
              const storeLevel = note.storeLevel;
              if (storeLevel !== undefined && storeLevel !== null) {
                // Convert to number if it's a string
                const storeLevelNum = typeof storeLevel === 'string' ? parseFloat(storeLevel) : Number(storeLevel);
                if (!isNaN(storeLevelNum)) {
                  if (storeLevelNum >= criteria.storeLevelRange.min && storeLevelNum <= criteria.storeLevelRange.max) {
                    matchesStoreLevel = true;
                  }
                }
              }
            }

            // Check low stock categories - normalize for case-insensitive matching
            if (criteria.lowStockCategories && criteria.lowStockCategories.length > 0 && !matchesLowStock) {
              const noteLowStock = note.lowStockCategories || [];
              // Convert to lowercase for case-insensitive comparison
              const noteLowStockLower = noteLowStock.map((c: string) => String(c).toLowerCase().trim());
              const criteriaLowStockLower = criteria.lowStockCategories.map(c => String(c).toLowerCase().trim());
              
              // Check if any category matches (case-insensitive)
              if (criteriaLowStockLower.some(c => noteLowStockLower.includes(c))) {
                matchesLowStock = true;
              }
            }

            // Check manager contact - handle undefined/null values properly
            if (criteria.needsManagerContact !== null && !matchesManagerContact) {
              const noteRequestContact = note.requestManagerContact === true || note.requestManagerContact === 'true';
              if (criteria.needsManagerContact === true && noteRequestContact) {
                matchesManagerContact = true;
              } else if (criteria.needsManagerContact === false && !noteRequestContact) {
                matchesManagerContact = true;
              }
            }

            // Check visit types - normalize for case-insensitive matching
            if (criteria.visitTypes && criteria.visitTypes.length > 0 && !matchesVisitTypes) {
              const noteVisitType = note.visitType;
              if (noteVisitType) {
                const noteVisitTypeLower = String(noteVisitType).toLowerCase().trim();
                const criteriaVisitTypesLower = criteria.visitTypes.map(vt => String(vt).toLowerCase().trim());
                if (criteriaVisitTypesLower.includes(noteVisitTypeLower)) {
                  matchesVisitTypes = true;
                }
              }
            }

            // Check visit date range - handle both createdAt and date fields
            if (criteria.visitDateRange && !matchesVisitDateRange) {
              let noteDate: any = note.createdAt || note.date;
              if (noteDate) {
                let noteDateValue: Date;
                if (noteDate.toDate && typeof noteDate.toDate === 'function') {
                  noteDateValue = noteDate.toDate();
                } else if (noteDate instanceof Date) {
                  noteDateValue = noteDate;
                } else if (typeof noteDate === 'string') {
                  // Try parsing date string (could be in various formats)
                  noteDateValue = new Date(noteDate);
                } else {
                  noteDateValue = new Date(noteDate);
                }
                
                // Check if date is valid
                if (!isNaN(noteDateValue.getTime())) {
                  const noteDateStr = noteDateValue.toISOString().split('T')[0]; // YYYY-MM-DD
                  
                  const fromDate = criteria.visitDateRange.from || '';
                  const toDate = criteria.visitDateRange.to || '';
                  
                  const inRange = (!fromDate || noteDateStr >= fromDate) && (!toDate || noteDateStr <= toDate);
                  if (inRange) {
                    matchesVisitDateRange = true;
                  }
                }
              }
            }

            // Check sales rep (by note author/salesRepName matching selected sales rep IDs)
            if (criteria.salesRepIds && criteria.salesRepIds.length > 0 && salesRepMap) {
              const noteAuthor = note.author || '';
              const noteSalesRepName = note.salesRepName || '';
              
              // Check if note author matches any selected sales rep
              const matchesRep = criteria.salesRepIds.some(repId => {
                const repInfo = salesRepMap.get(repId);
                if (!repInfo) return false;
                
                // Match by email (author field) or name (salesRepName field)
                const authorMatches = repInfo.email && noteAuthor.toLowerCase() === repInfo.email.toLowerCase();
                const nameMatches = repInfo.name && noteSalesRepName.toLowerCase().includes(repInfo.name.toLowerCase());
                
                return authorMatches || nameMatches;
              });
              
              if (matchesRep) {
                matchesSalesRep = true;
              }
            } else if (criteria.salesRepIds && criteria.salesRepIds.length > 0 && !salesRepMap) {
              // If no sales rep map provided, skip this filter (don't filter out stores)
              matchesSalesRep = true;
            }
          }

          // Store matches if all active criteria are satisfied
          const allMatch = matchesCompetitors && matchesStoreLevel && matchesLowStock && matchesManagerContact && matchesVisitTypes && matchesVisitDateRange && matchesSalesRep;
          
          if (allMatch) {
            console.log(`[StoreService] ✓ Store ${storeId} matches all criteria:`, {
              matchesCompetitors,
              matchesStoreLevel,
              matchesLowStock,
              matchesManagerContact,
              matchesVisitTypes,
              matchesVisitDateRange,
              matchesSalesRep,
              notesCount: notes.length
            });
            return storeId;
          } else {
            // Only log detailed debug info for first few non-matching stores to avoid console spam
            if (matchingStoreIds.length < 3 && notes.length > 0) {
              console.log(`[StoreService] ✗ Store ${storeId} did not match:`, {
                matchesCompetitors,
                matchesStoreLevel,
                matchesLowStock,
                matchesManagerContact,
                matchesVisitTypes,
                matchesVisitDateRange,
                matchesSalesRep,
                notesCount: notes.length,
                sampleNote: {
                  competitors: notes[0].competitors,
                  storeLevel: notes[0].storeLevel,
                  storeLevelType: typeof notes[0].storeLevel,
                  lowStockCategories: notes[0].lowStockCategories,
                  requestManagerContact: notes[0].requestManagerContact,
                  requestManagerContactType: typeof notes[0].requestManagerContact,
                  visitType: notes[0].visitType,
                  createdAt: notes[0].createdAt,
                  author: notes[0].author,
                  salesRepName: notes[0].salesRepName
                },
                criteria: {
                  competitors: criteria.competitors,
                  storeLevelRange: criteria.storeLevelRange,
                  lowStockCategories: criteria.lowStockCategories,
                  needsManagerContact: criteria.needsManagerContact,
                  visitTypes: criteria.visitTypes,
                  visitDateRange: criteria.visitDateRange,
                  salesRepIds: criteria.salesRepIds
                }
              });
            }
          }

          return null;
        } catch (error) {
          console.warn(`[StoreService] Error fetching notes for store ${storeId}:`, error);
          return null;
        }
      });

      const batchResults = await Promise.all(batchPromises);
      batchResults.forEach(result => {
        if (result) {
          matchingStoreIds.push(result);
        }
      });
    }

    console.log(`[StoreService] Filtered ${storeIds.length} stores to ${matchingStoreIds.length} matches in ${Date.now() - startTime}ms`);
    return matchingStoreIds;
  }
}

